import { Link, useNavigate } from "react-router-dom";
import React, { useState, useCallback, useEffect, useRef } from "react";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import FormInput from "../../../components/CustomComponents/Input/formInput";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import { GenderDropDownSelection } from "../../../components/CustomComponents/DropdownSelections/DropDownSelections";
import GridSectionLoader from "../../../components/CustomComponents/preLoad/gridSectionLoader";
import CountryCitySelector from "../../../components/CustomComponents/DropdownSelections/CountryCitySelector";
import CustomerGroupSelector from "../../../components/CustomComponents/DropdownSelections/CustomerGroupSelector";
import ToggleSwitch from "../../../components/CustomComponents/Switchery/switchery";
import { ThreeDropdownBirthdatePicker } from "../../../components/CustomComponents/DatePicker/DatePicker";
import { useSelector } from "react-redux";
import showErrorToast, {
  showSuccessToast,
} from "../../../components/CustomComponents/toast/toast";
import { Panel, PanelHeader, PanelBody } from "../../../components/panel/panel";
import { formatDialCode, toE164Phone } from "../../../utils/phoneUtils";
import "./updateCustomer.css";
import SaveActions from "../../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../../hooks/useUnsavedChangesGuard.js";

const initialCustomerData = {
  Cust_Fname: "",
  Cust_Lname: "",
  Cust_Address: "",
  Cust_City: "",
  Cust_Country: "UK",
  Cust_Mobile: "",
  Cust_Phone: "",
  Cust_ActivationCode: "",
  Cust_ActivationCodeCount: 0,
  Password: "",
  Cust_HouseNo: "",
  Cust_Email: "",
  Cust_PostCode: "",
  CompID: 1,
  Cust_datetime: new Date(),
  Cust_LoyaltyPoints: 0,
  SignupOrigon: "",
  CustomerStripID: "",
  Cust_Title: "",
  Cust_Gender: "",
  Cust_BusinessName: "",
  Cust_DOB: null,
  Cust_MaxCredit: 0.0,
  Cust_CurrentBalance: 0.0,
  Cus_Note: "",
  Cust_IsCommunicationViaEmail: false,
  Cust_IsCommunicationViaText: false,
  Cust_IsCommunicationViaPhone: false,
  Cust_Opt_In_Marketing: false,
  Cust_Reference_ID: "",
  Cust_VAT_Number: "",
  Cust_LastOrderType: "",
  Cust_AverageOrderCount: 0,
  Cust_AverageOrderSpent: 0.0,
  Cust_EnableLoyalty: false,
  Cust_Deposit: 0.0,
  Cus_LastOrderDate: null,
  Cust_TotalOrderCount: 0,
  Cust_TotalOrderSpent: 0.0,
  Cust_TotalEarnedLoyaltyPoints: 0,
  Cust_TotalSpentLoyaltyPoints: 0,
  Cust_GroupID: 0,
};

const PREFERENCE_TOGGLES = [
  { name: "Cust_IsCommunicationViaEmail", label: "Via Email" },
  { name: "Cust_IsCommunicationViaText", label: "Via Text" },
  { name: "Cust_IsCommunicationViaPhone", label: "Via Phone" },
  { name: "Cust_Opt_In_Marketing", label: "Marketing Opt-in" },
  { name: "Cust_EnableLoyalty", label: "Enable Loyalty" },
];

function AddCustomer() {
  const [selectedCity, setSelectedCity] = useState(null);
  const navigate = useNavigate();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(initialCustomerData);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [countryCallCodeMap, setCountryCallCodeMap] = useState({});
  const [companyCountry, setCompanyCountry] = useState("");
  const [companyCallCode, setCompanyCallCode] = useState("");

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      Cust_Fname: customerData.Cust_Fname || "",
      Cust_Lname: customerData.Cust_Lname || "",
      Cust_Email: customerData.Cust_Email || "",
      Cust_Mobile: customerData.Cust_Mobile || "",
      Cust_Phone: customerData.Cust_Phone || "",
      Cust_Address: customerData.Cust_Address || "",
      Cust_HouseNo: customerData.Cust_HouseNo || "",
      Cust_PostCode: customerData.Cust_PostCode || "",
      Cust_Country: customerData.Cust_Country || "",
      Cust_City: selectedCity || "",
      Cust_Gender: customerData.Cust_Gender || "",
      Cust_DOB: customerData.Cust_DOB || null,
      Cus_Note: customerData.Cus_Note || "",
      Cust_GroupID: selectedGroup || null,
      Cust_IsCommunicationViaEmail: Boolean(customerData.Cust_IsCommunicationViaEmail),
      Cust_IsCommunicationViaText: Boolean(customerData.Cust_IsCommunicationViaText),
      Cust_IsCommunicationViaPhone: Boolean(customerData.Cust_IsCommunicationViaPhone),
      Cust_Opt_In_Marketing: Boolean(customerData.Cust_Opt_In_Marketing),
      Cust_EnableLoyalty: Boolean(customerData.Cust_EnableLoyalty),
      Cust_Reference_ID: customerData.Cust_Reference_ID || "",
      Cust_VAT_Number: customerData.Cust_VAT_Number || "",
      Cust_BusinessName: customerData.Cust_BusinessName || "",
      Cust_Title: customerData.Cust_Title || "",
      Cust_MaxCredit: customerData.Cust_MaxCredit ?? 0,
    });
  }, [customerData, selectedCity, selectedGroup]);

  useEffect(() => {
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [getFormSnapshot]);

  const isDirty =
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  const companyDialCode = formatDialCode(
    companyCallCode ||
      countryCallCodeMap[companyCountry] ||
      countryCallCodeMap[(companyCountry || "").toUpperCase()] ||
      ""
  );

  useEffect(() => {
    let cancelled = false;
    const bootstrapCountry = async () => {
      try {
        const [countriesRes, profileRes] = await Promise.all([
          axiosInstance.get(APIs.GET_COUNTRIES),
          axiosInstance.get(APIs.GET_COMPANY_PROFILE),
        ]);
        if (cancelled) return;

        const list = countriesRes.data?.data || countriesRes.data || [];
        const map = {};
        (Array.isArray(list) ? list : []).forEach((country) => {
          const callCode = country.callCode ?? country.CallCode ?? "";
          const code = country.code ?? country.Code ?? "";
          const code2 = country.code2 ?? country.Code2 ?? "";
          const name = country.name ?? country.Name ?? "";
          if (code) map[code] = callCode;
          if (code2) map[code2] = callCode;
          if (name) map[name] = callCode;
        });
        setCountryCallCodeMap(map);

        const profile = profileRes.data?.data || {};
        const profileCountry = profile.compCountry || profile.CompCountry || "";
        const profileCallCode = profile.callCode || profile.CallCode || "";
        if (profileCallCode) setCompanyCallCode(profileCallCode);
        if (profileCountry) {
          setCompanyCountry(profileCountry);
          setCustomerData((prev) => ({
            ...prev,
            Cust_Country: prev.Cust_Country === "UK" && profileCountry
              ? profileCountry
              : prev.Cust_Country || profileCountry,
          }));
        }
      } catch {
        // Non-blocking
      }
    };
    bootstrapCountry();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleInputChange = useCallback((e, isDropdown = false) => {
    if (isDropdown) {
      const { name, value } = e;
      setCustomerData((prev) => ({
        ...prev,
        [name]: value || "",
      }));
    } else {
      const { name, value, type, checked } = e.target;
      setCustomerData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  }, []);

  const handleToggleChange = useCallback((checked, name) => {
    setCustomerData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }, []);

  const submitCustomer = async (submitData, exitAfterSave) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        APIs.POST_ADD_UPDATE_CUSTOMER,
        submitData
      );

      if (response.data.status) {
        showSuccessToast(
          response.data.message || "Customer Successfully Created"
        );
        initialFormSnapshotRef.current = getFormSnapshot();
        setIsSubmitted(true);
        if (exitAfterSave) {
          navigate("/customer/manageCustomer");
        }
        return true;
      }
      showErrorToast(response.data.message || "Error Creating Customer");
      return false;
    } catch (err) {
      if (err.response) {
        showErrorToast(
          err.response.data.message || "Error submitting customer"
        );
      } else if (err.request) {
        showErrorToast("No response received from server");
      } else {
        showErrorToast("Error in customer submission");
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;

    if (!customerData.Cust_Fname.trim()) {
      showErrorToast("Please provide a valid first name");
      return false;
    }

    if (!customerData.Cust_Email || !validateEmail(customerData.Cust_Email)) {
      showErrorToast("Please provide a valid email");
      return false;
    }

    if (customerData.Cust_Country && !selectedCity) {
      showErrorToast("Please select a city for the chosen country");
      return false;
    }

    if (!selectedGroup) {
      showErrorToast("Please Select a customer group");
      return false;
    }

    const mobileRaw = (customerData.Cust_Mobile || "").trim();
    let mobileE164 = "";
    if (mobileRaw) {
      if (!companyDialCode && !mobileRaw.startsWith("+")) {
        showErrorToast(
          "Company dial code is not available. Enter mobile in E.164 format, e.g. +447911123456."
        );
        return false;
      }
      mobileE164 = toE164Phone(mobileRaw, companyDialCode);
      if (!mobileE164.startsWith("+") || mobileE164.length < 8) {
        showErrorToast(
          "Please enter a valid mobile number (digits only; country code is added automatically)."
        );
        return false;
      }
    }

    const submitData = {
      ...customerData,
      Cust_Mobile: mobileE164,
      Cust_DOB: customerData.Cust_DOB || null,
      Cust_datetime: new Date(),
      Cust_City: selectedCity,
      Cust_GroupID: selectedGroup,
    };

    submitData.CompID = CompID;
    return submitCustomer(submitData, exitAfterSave);
  };

  const handleBirthdateChange = useCallback((birthdateInfo) => {
    setCustomerData((prev) => ({
      ...prev,
      Cust_DOB: birthdateInfo?.formattedDate ?? null,
    }));
  }, []);

  useEffect(() => {
    if (!isSubmitted) return;
    const timer = setTimeout(() => setIsSubmitted(false), 3000);
    return () => clearTimeout(timer);
  }, [isSubmitted]);

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  const RequiredLabel = ({ text }) => (
    <label className="form-label">
      {text} <span className="text-danger">*</span>
    </label>
  );

  const NormalLabel = ({ text }) => (
    <label className="form-label">{text}</label>
  );

  const sectionStyle = {
    borderBottom: `2px solid ${themeColor || "var(--bs-border-color)"}`,
  };

  const submitStyle = themeColor
    ? {
        backgroundColor: themeColor,
        background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
        boxShadow: `0 2px 8px ${themeColor}40`,
      }
    : undefined;

  return (
    <div className="customer-page">
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/customer/manageCustomer">Manage Customers</Link>
            </li>
            <li className="breadcrumb-item active">Add Customer</li>
          </ol>
          <h1 className="page-header mb-0">Add Customer</h1>
        </div>
        <SaveActions
          onCancel={() => navigate("/customer/manageCustomer")}
          onSave={() => handleSubmit({ exitAfterSave: false })}
          onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
          saving={loading}
        />
      </div>

      <GridSectionLoader loading={loading} minHeight="280px">
        <div className="card border-0">
          <Panel className="m-3">
            <PanelHeader>Customer Details</PanelHeader>
            <PanelBody>
              <h6 className="customer-section-heading" style={sectionStyle}>
                Personal
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <RequiredLabel text="First Name" />
                  <FormInput
                    name="Cust_Fname"
                    placeholder="Enter First Name"
                    type="text"
                    value={customerData.Cust_Fname}
                    onchange={handleInputChange}
                  />
                </div>
                <div className="col-md-6">
                  <NormalLabel text="Last Name" />
                  <FormInput
                    name="Cust_Lname"
                    placeholder="Enter Last Name"
                    type="text"
                    value={customerData.Cust_Lname}
                    onchange={handleInputChange}
                  />
                </div>
                <div className="col-md-6">
                  <RequiredLabel text="Email" />
                  <FormInput
                    name="Cust_Email"
                    placeholder="Enter Email"
                    type="email"
                    value={customerData.Cust_Email}
                    onchange={handleInputChange}
                  />
                </div>
                <div className="col-md-6">
                  <NormalLabel text="Password" />
                  <FormInput
                    name="Password"
                    placeholder="Enter Password"
                    type="password"
                    value={customerData.Password}
                    onchange={handleInputChange}
                  />
                </div>
                <div className="col-md-6">
                  <NormalLabel text="Date of Birth" />
                  <ThreeDropdownBirthdatePicker
                    onDateChange={handleBirthdateChange}
                    initialDate={customerData.Cust_DOB}
                  />
                </div>
                <div className="col-md-6">
                  <NormalLabel text="Gender" />
                  <GenderDropDownSelection
                    selectedValue={customerData.Cust_Gender}
                    onChange={(option) =>
                      handleInputChange({
                        target: {
                          name: "Cust_Gender",
                          value: option?.value || "",
                        },
                      })
                    }
                  />
                </div>
                <div className="col-md-6">
                  <NormalLabel text="Mobile Number" />
                  <FormInput
                    name="Cust_Mobile"
                    placeHolder={
                      companyDialCode
                        ? "Enter number without country code"
                        : "Enter Mobile Number e.g. +447911123456"
                    }
                    type="text"
                    value={customerData.Cust_Mobile}
                    onchange={handleInputChange}
                    prepend={companyDialCode || undefined}
                  />
                </div>
                <div className="col-md-6">
                  <RequiredLabel text="Customer Group" />
                  <CustomerGroupSelector
                    selectedGroup={selectedGroup}
                    setSelectedGroup={setSelectedGroup}
                    onChange={(value) => setSelectedGroup(value)}
                    setLoading={setLoading}
                  />
                </div>
              </div>

              <h6 className="customer-section-heading" style={sectionStyle}>
                Address
              </h6>
              <div className="row g-3">
                <div className="col-md-8">
                  <NormalLabel text="Address" />
                  <FormInput
                    name="Cust_Address"
                    placeholder="Enter Address"
                    type="text"
                    value={customerData.Cust_Address}
                    onchange={handleInputChange}
                  />
                </div>
                <div className="col-md-4">
                  <NormalLabel text="House Number" />
                  <FormInput
                    name="Cust_HouseNo"
                    placeholder="House No."
                    type="text"
                    value={customerData.Cust_HouseNo}
                    onchange={handleInputChange}
                  />
                </div>
                <div className="col-12">
                  <CountryCitySelector
                    selectedCountry={customerData.Cust_Country}
                    selectedCity={selectedCity}
                    setSelectedCity={setSelectedCity}
                    onChange={(updatedData) =>
                      setCustomerData((prev) => ({ ...prev, ...updatedData }))
                    }
                    setLoading={setLoading}
                    isDarkMode={isDarkMode}
                    selectedValueIsLabel={true}
                    countryFieldName="Cust_Country"
                    cityFieldName="Cust_City"
                  />
                </div>
                <div className="col-md-6">
                  <NormalLabel text="Post Code" />
                  <FormInput
                    name="Cust_PostCode"
                    placeholder="Enter Post Code"
                    type="text"
                    value={customerData.Cust_PostCode}
                    onchange={handleInputChange}
                  />
                </div>
              </div>

              <h6 className="customer-section-heading" style={sectionStyle}>
                Preferences
              </h6>
              <div className="customer-pref-row mb-3">
                {PREFERENCE_TOGGLES.map((item) => (
                  <ToggleSwitch
                    key={item.name}
                    isChecked={customerData[item.name] || false}
                    onToggle={(checked) =>
                      handleToggleChange(checked, item.name)
                    }
                    name={item.name}
                    label={item.label}
                  />
                ))}
              </div>

              <h6 className="customer-section-heading" style={sectionStyle}>
                Additional
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <NormalLabel text="Reference ID" />
                  <FormInput
                    name="Cust_Reference_ID"
                    placeholder="Enter Reference ID"
                    type="text"
                    value={customerData.Cust_Reference_ID}
                    onchange={handleInputChange}
                  />
                </div>
                <div className="col-md-6">
                  <NormalLabel text="VAT Number" />
                  <FormInput
                    name="Cust_VAT_Number"
                    placeholder="Enter VAT Number"
                    type="text"
                    value={customerData.Cust_VAT_Number}
                    onchange={handleInputChange}
                  />
                </div>
              </div>
            </PanelBody>
          </Panel>
        </div>
      </GridSectionLoader>
    </div>
  );
}

export default AddCustomer;
